import { useCallback, useMemo, useState, type ReactNode } from 'react';
import {
  DSButton,
  DSDrawer,
  DSIcon,
  DSModal,
  DSTextInput,
  DSTypography,
  DsSelect,
  type DSIconName
} from '@eiv2/design-system';
import { DataGrid, type GridColDef, type GridPaginationModel } from '@mui/x-data-grid';
import { remarketingApi, type ListJourneysParams } from '../../api/client';
import { useCompactViewport } from '../../hooks/useCompactViewport';
import { usePaginatedList } from '../../hooks/usePaginatedList';
import { LoadMoreButton } from '../LoadMoreButton';
import { getDefaultWindow } from '../../services/dateWindow';
import { formatCurrencyFromCents, formatDateTime, formatNumber } from '../../services/format';
import { dataGridLocaleText } from '../../theme/dataGridLocale';
import type {
  RemarketingJourney,
  RemarketingJourneyListResponse,
  RemarketingJourneyStep,
  RemarketingJourneyStatus,
  RemarketingPaymentMethod
} from '../../types/api';
import { DateRangeFields, DateRangeValidationMessage } from '../DateRangePicker';
import { CollapsibleFiltersCard } from '../CollapsibleFiltersCard';
import { DataGridFrame, visibleDataGridRows } from '../DataGridFrame';
import { IconTooltipButton } from '../IconTooltipButton';
import { RequestState } from '../RequestState';
import { SectionCard } from '../SectionCard';
import {
  apiErrorCode,
  EventRequiredState,
  eventLabel,
  OrganizerEventFilter,
  type OrganizerEventScope
} from './OrganizerEventFilter';

const stepLabels: Record<RemarketingJourneyStep, string> = {
  started: 'Início',
  ticket_selection: 'Seleção de ingressos',
  optionals: 'Opcionais',
  identification: 'Identificação',
  payment_selection: 'Escolha do pagamento'
};
const statusLabels: Record<RemarketingJourneyStatus, string> = {
  open: 'Em andamento',
  pending_payment: 'Aguardando pagamento',
  converted: 'Convertida',
  canceled: 'Cancelada',
  expired: 'Expirada'
};
const paymentLabels: Record<RemarketingPaymentMethod, string> = {
  credit_card: 'Cartão de crédito',
  pix: 'Pix',
  boleto: 'Boleto',
  free: 'Gratuito'
};
const missing = 'Não informado';

const stepOptions = [
  { value: '', label: 'Todas' },
  ...Object.entries(stepLabels).map(([value, label]) => ({ value, label }))
];
const statusOptions = [
  { value: '', label: 'Todas' },
  ...Object.entries(statusLabels).map(([value, label]) => ({ value, label }))
];
const contactOptions = [
  { value: '', label: 'Todos' },
  { value: 'true', label: 'Com e-mail ou telefone' },
  { value: 'false', label: 'Sem e-mail e telefone' }
];

type StatusTone = {
  className: string;
  icon: DSIconName;
};

function toneFor(status: RemarketingJourneyStatus): StatusTone {
  if (status === 'converted') {
    return { className: 'bg-feedback-success/20 text-feedback-success', icon: 'TickCircle' };
  }
  if (status === 'pending_payment') {
    return { className: 'bg-brand-primary-3-20 text-brand-primary-3', icon: 'Card' };
  }
  if (status === 'canceled') {
    return { className: 'bg-feedback-danger/20 text-feedback-danger', icon: 'CloseCircle' };
  }
  if (status === 'expired') {
    return { className: 'bg-gray-3 text-dark-3', icon: 'Timer1' };
  }
  // open — Em andamento (azul, conforme pedido)
  return { className: 'bg-[#2F6FED]/15 text-[#2F6FED]', icon: 'Flash' };
}

function JourneyStatus({ status }: { status: RemarketingJourneyStatus }) {
  const tone = toneFor(status);

  return (
    <span
      className={`inline-flex shrink-0 items-center justify-center gap-1 rounded-2xl py-0.5 pr-2 pl-1.5 ${tone.className}`}
    >
      <DSIcon aria-hidden name={tone.icon} size={12} />
      <span className="whitespace-nowrap text-[12px] font-medium leading-[18px]">
        {statusLabels[status] ?? status}
      </span>
    </span>
  );
}

function DetailValue({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="min-w-0">
      <DSTypography as="dt" className="text-[12px] font-semibold text-gray-5">
        {label}
      </DSTypography>
      <DSTypography as="dd" className="m-0 text-[14px] font-normal break-words text-dark-3">
        {value || missing}
      </DSTypography>
    </div>
  );
}

function JourneyDetailsBody({
  journey,
  onClose,
  layout,
  eventScope
}: {
  journey: RemarketingJourney;
  onClose: () => void;
  layout: 'modal' | 'drawer';
  eventScope?: OrganizerEventScope;
}) {
  return (
    <div className="flex max-h-[min(85vh,720px)] flex-col">
      <div
        className={[
          'flex shrink-0 items-center gap-3 border-b border-gray-3 px-6 py-4',
          layout === 'modal' ? 'justify-between' : ''
        ].join(' ')}
      >
        <DSTypography as="h2" className="text-[18px] font-semibold text-dark-3" id="journey-details-title">
          Detalhes da jornada
        </DSTypography>
        {layout === 'modal' ? (
          <DSButton
            aria-label="Fechar detalhes"
            buttonType="icon"
            onClick={onClose}
            type="button"
            variant="tertiary"
          >
            <DSIcon name="CloseCircle" size={20} />
          </DSButton>
        ) : null}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
        <div className="mb-4">
          <JourneyStatus status={journey.status} />
        </div>

        <div className="flex flex-col gap-6">
          <section>
            <DSTypography as="h3" className="mb-3 text-[15px] font-semibold text-dark-3">
              Contato
            </DSTypography>
            <dl className="m-0 flex flex-col gap-3">
              <DetailValue label="Nome" value={journey.contact.name} />
              <DetailValue label="E-mail" value={journey.contact.email} />
              <DetailValue label="Telefone" value={journey.contact.phone} />
            </dl>
          </section>

          <hr className="border-gray-3" />

          <section>
            <DSTypography as="h3" className="mb-3 text-[15px] font-semibold text-dark-3">
              Último estado registrado
            </DSTypography>
            <dl className="m-0 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <DetailValue label="Etapa" value={stepLabels[journey.step] ?? journey.step} />
              <DetailValue
                label="Pagamento"
                value={journey.paymentMethod ? paymentLabels[journey.paymentMethod] : null}
              />
              <DetailValue
                label="Evento"
                value={eventScope ? eventLabel(eventScope.events, journey.eventId) : journey.eventId}
              />
              {eventScope ? null : (
                <DetailValue label="Organização" value={journey.organizationId} />
              )}
              <DetailValue label="Origem" value={journey.source} />
              <DetailValue label="Última atividade" value={formatDateTime(journey.lastActivityAt)} />
              <DetailValue label="Criada em" value={formatDateTime(journey.createdAt)} />
              <DetailValue label="Atualizada em" value={formatDateTime(journey.updatedAt)} />
            </dl>
          </section>

          <hr className="border-gray-3" />

          <section>
            <DSTypography as="h3" className="mb-3 text-[15px] font-semibold text-dark-3">
              Carrinho informado
            </DSTypography>
            {journey.cart === null ? (
              <DSTypography as="p" className="text-[13px] font-normal text-gray-5">
                Carrinho não informado.
              </DSTypography>
            ) : journey.cart.items.length === 0 ? (
              <DSTypography as="p" className="text-[13px] font-normal text-gray-5">
                Nenhum item no carrinho.
              </DSTypography>
            ) : (
              <ul className="m-0 flex list-none flex-col gap-4 p-0">
                {journey.cart.items.map((item, index) => (
                  <li key={`${item.kind}:${item.id}:${index}`} className="border-b border-gray-3 pb-3">
                    <DSTypography as="p" className="text-[14px] font-semibold break-words text-dark-3">
                      {item.label || item.id}
                    </DSTypography>
                    <DSTypography as="p" className="text-[12px] font-normal text-gray-5">
                      {item.kind === 'ticket' ? 'Ingresso' : 'Opcional'} · Quantidade:{' '}
                      {formatNumber(item.quantity)}
                    </DSTypography>
                    <DSTypography as="p" className="mt-1 text-[13px] font-normal text-dark-3">
                      Valor:{' '}
                      {item.amountCents === undefined
                        ? missing
                        : formatCurrencyFromCents(item.amountCents)}
                    </DSTypography>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <hr className="border-gray-3" />

          <dl className="m-0 flex flex-col gap-3">
            <DetailValue label="Jornada" value={journey.id} />
            <DetailValue label="Lead vinculado" value={journey.leadId} />
            <DetailValue label="Referência do pedido" value={journey.orderRef} />
          </dl>
        </div>
      </div>

      {layout === 'drawer' ? (
        <div className="shrink-0 border-t border-gray-3 p-6">
          <DSButton className="w-full" onClick={onClose} type="button" variant="secondary">
            Fechar
          </DSButton>
        </div>
      ) : null}
    </div>
  );
}

function JourneyDetails({
  journey,
  onClose,
  isMobile,
  eventScope
}: {
  journey: RemarketingJourney | null;
  onClose: () => void;
  isMobile: boolean;
  eventScope?: OrganizerEventScope;
}) {
  const open = journey !== null;
  const layout = isMobile ? 'drawer' : 'modal';
  const body: ReactNode = journey ? (
    <JourneyDetailsBody eventScope={eventScope} journey={journey} layout={layout} onClose={onClose} />
  ) : null;

  if (isMobile) {
    return (
      <DSDrawer
        aria-labelledby="journey-details-title"
        className="flex max-h-[calc(100dvh-40px)] w-full flex-col overflow-hidden rounded-t-[24px] rounded-b-none"
        onClose={onClose}
        open={open}
        orientation="bottom"
        overlayClassName="bg-overlay/45"
        size="fit"
      >
        <div className="flex h-6 shrink-0 items-center justify-center" aria-hidden>
          <span className="h-1 w-10 rounded-full bg-gray-3" />
        </div>
        {body}
      </DSDrawer>
    );
  }

  return (
    <DSModal
      aria-labelledby="journey-details-title"
      className="max-w-[640px] overflow-hidden p-0"
      onClose={onClose}
      open={open}
      overlayClassName="bg-overlay/45"
    >
      {body}
    </DSModal>
  );
}

export interface RemarketingJourneysProps {
  /** Fonte das jornadas. Padrão: BFF administrativo (`/api/remarketing/journeys`). */
  listJourneys?: (
    params: ListJourneysParams,
    signal?: AbortSignal
  ) => Promise<RemarketingJourneyListResponse>;
  /**
   * Área do organizador: organização fixa (sem campo de organização) e filtro
   * pelos eventos autorizados. Ausente = painel administrativo.
   */
  eventScope?: OrganizerEventScope;
}

export function RemarketingJourneys({
  listJourneys = remarketingApi.listJourneys,
  eventScope
}: RemarketingJourneysProps = {}) {
  const [dateWindow, setWindow] = useState(() => getDefaultWindow());
  const [draft, setDraft] = useState<
    Pick<ListJourneysParams, 'eventId' | 'organizationId' | 'step' | 'status' | 'hasContact'>
  >({});
  const [filters, setFilters] = useState<ListJourneysParams>(() => ({
    activityFrom: dateWindow.from,
    activityTo: dateWindow.to
  }));
  const [selected, setSelected] = useState<RemarketingJourney | null>(null);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25
  });
  // 422 `event_filter_required`: a API tem eventos demais para consultar sem filtro.
  const [eventFilterRequired, setEventFilterRequired] = useState(false);
  // Remonta o filtro de evento do organizador (e o ID digitado) ao limpar.
  const [eventFilterKey, setEventFilterKey] = useState(0);
  const compact = useCompactViewport();
  const invalidRange = Date.parse(dateWindow.to) <= Date.parse(dateWindow.from);
  const requiresEvent = !!eventScope && (eventScope.eventsTruncated || eventFilterRequired);
  // Sem o evento exigido não há consulta — e nada de lista vazia fingindo resultado.
  const blocked = requiresEvent && !filters.eventId;

  const fetcher = useCallback(
    async (cursor: string | null, signal: AbortSignal) => {
      if (blocked) return { data: [], nextCursor: null };
      try {
        const page = await listJourneys({ ...filters, limit: 50, cursor }, signal);
        return { data: page.journeys, nextCursor: page.nextCursor };
      } catch (err) {
        if (eventScope && apiErrorCode(err) === 'event_filter_required') setEventFilterRequired(true);
        throw err;
      }
    },
    [filters, blocked, listJourneys, eventScope]
  );
  const list = usePaginatedList(fetcher, [filters, blocked]);

  const columns = useMemo(() => {
    const definitions: GridColDef<RemarketingJourney>[] = [
      {
        field: 'contact',
        headerName: 'Contato',
        minWidth: 220,
        flex: 1,
        renderCell: ({ row }) => (
          <div className="flex h-full min-w-0 flex-col justify-center py-2">
            <DSTypography
              as="p"
              className="text-[13px] font-semibold break-words whitespace-normal text-dark-3"
            >
              {row.contact.name || missing}
            </DSTypography>
            <DSTypography
              as="p"
              className="text-[12px] font-normal break-words whitespace-normal text-gray-5"
            >
              {row.contact.email || 'E-mail não informado'}
            </DSTypography>
            <DSTypography as="p" className="text-[12px] font-normal text-gray-5">
              {row.contact.phone || 'Telefone não informado'}
            </DSTypography>
            {compact ? (
              <div className="mt-2">
                <JourneyStatus status={row.status} />
              </div>
            ) : null}
          </div>
        )
      },
      eventScope
        ? {
            field: 'eventId',
            headerName: 'Evento',
            width: 180,
            renderCell: ({ row }) => {
              const title = eventScope.events.find((event) => event.id === row.eventId)?.title;
              return (
                <div>
                  <DSTypography as="p" className="text-[13px] font-semibold break-words whitespace-normal text-dark-3">
                    {title || `Evento ${row.eventId}`}
                  </DSTypography>
                  <DSTypography as="p" className="text-[12px] font-normal text-gray-5">
                    ID {row.eventId}
                  </DSTypography>
                </div>
              );
            }
          }
        : {
            field: 'eventId',
            headerName: 'Evento / Org.',
            width: 125,
            renderCell: ({ row }) => (
              <div>
                <DSTypography as="p" className="text-[13px] font-semibold text-dark-3">
                  {row.eventId}
                </DSTypography>
                <DSTypography as="p" className="text-[12px] font-normal text-gray-5">
                  Org.: {row.organizationId || missing}
                </DSTypography>
              </div>
            )
          },
      {
        field: 'step',
        headerName: 'Etapa',
        width: 150,
        valueFormatter: (value: RemarketingJourneyStep) => stepLabels[value] ?? value
      },
      {
        field: 'status',
        headerName: 'Situação',
        width: 200,
        renderCell: ({ row }) => <JourneyStatus status={row.status} />
      },
      {
        field: 'paymentMethod',
        headerName: 'Pagamento',
        width: 120,
        valueFormatter: (value: RemarketingPaymentMethod | null) =>
          value ? paymentLabels[value] : missing
      },
      {
        field: 'lastActivityAt',
        headerName: 'Última atividade',
        width: 150,
        valueFormatter: (value: string) => formatDateTime(value)
      },
      {
        field: 'details',
        headerName: '',
        width: 56,
        align: 'center',
        renderCell: ({ row }) => (
          <IconTooltipButton
            label="Ver detalhes"
            onClick={() => setSelected(row)}
            variant="ghost"
          >
            <DSIcon name="Eye" size={18} />
          </IconTooltipButton>
        )
      }
    ];
    return definitions.map((column) => ({ ...column, sortable: false, filterable: false }));
  }, [compact, eventScope]);

  function applyFilters() {
    if (invalidRange) return;
    setSelected(null);
    setFilters({
      ...draft,
      eventId: draft.eventId?.trim(),
      organizationId: eventScope ? undefined : draft.organizationId?.trim(),
      activityFrom: dateWindow.from,
      activityTo: dateWindow.to
    });
  }

  return (
    <div className="flex flex-col gap-6">
      <CollapsibleFiltersCard storageKey="remarketing-journeys" title="Filtrar jornadas">
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            applyFilters();
          }}
        >
          <div className="flex flex-col gap-3">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
              <DateRangeFields
                value={dateWindow}
                onChange={(next) => {
                  setWindow(next);
                  if (Date.parse(next.to) <= Date.parse(next.from)) return;
                  setSelected(null);
                  setFilters((current) => ({
                    ...current,
                    activityFrom: next.from,
                    activityTo: next.to
                  }));
                }}
              />
              {eventScope ? (
                <OrganizerEventFilter
                  key={eventFilterKey}
                  onChange={(eventId) => setDraft({ ...draft, eventId: eventId || undefined })}
                  required={requiresEvent}
                  scope={eventScope}
                  value={draft.eventId ?? ''}
                />
              ) : (
                <>
                  <DSTextInput
                    classNames={{ field: 'min-h-14 bg-light-gray' }}
                    label="Evento"
                    onChange={(e) => setDraft({ ...draft, eventId: e.target.value })}
                    value={draft.eventId ?? ''}
                  />
                  <DSTextInput
                    classNames={{ field: 'min-h-14 bg-light-gray' }}
                    label="Organização"
                    onChange={(e) => setDraft({ ...draft, organizationId: e.target.value })}
                    value={draft.organizationId ?? ''}
                  />
                </>
              )}
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <DsSelect
                onChange={(next) =>
                  setDraft({
                    ...draft,
                    step: ((typeof next === 'string' ? next : '') ||
                      undefined) as ListJourneysParams['step']
                  })
                }
                options={stepOptions}
                placeholder="Etapa"
                value={draft.step ?? ''}
              />
              <DsSelect
                onChange={(next) =>
                  setDraft({
                    ...draft,
                    status: ((typeof next === 'string' ? next : '') ||
                      undefined) as ListJourneysParams['status']
                  })
                }
                options={statusOptions}
                placeholder="Situação"
                value={draft.status ?? ''}
              />
              <DsSelect
                onChange={(next) =>
                  setDraft({
                    ...draft,
                    hasContact: ((typeof next === 'string' ? next : '') ||
                      undefined) as ListJourneysParams['hasContact']
                  })
                }
                options={contactOptions}
                placeholder="E-mail ou telefone"
                value={draft.hasContact ?? ''}
              />
            </div>
            <DateRangeValidationMessage
              value={dateWindow}
              onChange={(next) => setWindow(next)}
            />
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-end">
            <DSButton
              className="w-full sm:w-auto"
              disabled={invalidRange || list.isLoading}
              type="submit"
            >
              <DSIcon name="Filter" size={16} />
              Filtrar
            </DSButton>
            <DSButton
              className="w-full sm:w-auto"
              disabled={invalidRange || list.isLoading}
              onClick={() => {
                setDraft({});
                setEventFilterKey((key) => key + 1);
                setSelected(null);
                setFilters({ activityFrom: dateWindow.from, activityTo: dateWindow.to });
              }}
              type="button"
              variant="secondary"
            >
              Limpar filtros
            </DSButton>
          </div>
        </form>
      </CollapsibleFiltersCard>

      {blocked && eventScope ? (
        <EventRequiredState truncated={eventScope.eventsTruncated} />
      ) : list.error ? (
        <RequestState
          title="Não foi possível carregar as jornadas"
          error={list.error}
          onRetry={list.reload}
        />
      ) : list.isLoading && list.items.length === 0 ? (
        <RequestState title="Carregando jornadas" message="Buscando jornadas no período." />
      ) : (
        <SectionCard
          action={
            <IconTooltipButton
              disabled={list.isLoading}
              label="Atualizar"
              onClick={() => {
                setSelected(null);
                list.reload();
              }}
            >
              <DSIcon name="Refresh" size={20} />
            </IconTooltipButton>
          }
          subtitle={`${formatNumber(list.items.length)} ${list.items.length === 1 ? 'jornada carregada' : 'jornadas carregadas'}`}
          title="Jornadas"
        >
          <DataGridFrame
            rowHeight={88}
            visibleRowCount={visibleDataGridRows(
              list.items.length,
              paginationModel.page,
              paginationModel.pageSize
            )}
          >
            <DataGrid
              rows={list.items}
              columns={columns}
              disableColumnMenu
              disableRowSelectionOnClick
              getRowHeight={() => 'auto'}
              getEstimatedRowHeight={() => 88}
              columnVisibilityModel={
                compact
                  ? {
                      step: false,
                      status: false,
                      paymentMethod: false,
                      lastActivityAt: false,
                      eventId: false
                    }
                  : {}
              }
              pageSizeOptions={[10, 25, 50]}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              localeText={{
                ...dataGridLocaleText,
                noRowsLabel: 'Nenhuma jornada com esses filtros.'
              }}
            />
          </DataGridFrame>
          <LoadMoreButton
            nextCursor={list.nextCursor}
            onLoadMore={list.loadMore}
            isLoading={list.isLoading || list.isLoadingMore}
          />
        </SectionCard>
      )}

      <JourneyDetails
        eventScope={eventScope}
        isMobile={compact}
        journey={selected}
        onClose={() => setSelected(null)}
      />
    </div>
  );
}
